import React, { useCallback } from 'react';
import { Field } from 'react-final-form';

import * as Types from '../types';

export const withFinalFormField =
    <FieldValue, >(options: { type?: string } = {}) =>
        <P extends object>(Wrapped: React.ComponentType<P>) =>
            (props: P & Types.TFinalFormFieldProps<FieldValue>) => {
                const {
                    // react final form Field component only props
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    render, component,
                    afterSubmit, allowNull, beforeSubmit, data, defaultValue, format,
                    formatOnBlur, initialValue, isEqual, multiple, name, parse, ref, subscription,
                    type, validate, validateFields, value,
                    // handlers to be combined with react final form handlers
                    onChange, onBlur, onFocus,
                    // props for raw form field components
                    ...formFieldProps
                } = props;

                const finalFormFieldProps = {
                    afterSubmit, allowNull, beforeSubmit, data, defaultValue, format,
                    formatOnBlur, initialValue, isEqual, multiple, name, parse, ref, subscription,
                    type, validate, validateFields, value
                };

                return (
                    <Field {...finalFormFieldProps} type={type || options.type}>
                        {(renderProps) => {
                            const { input, meta } = renderProps;
                            const onCombinedBlur: Types.TOnCombinedBlur = useCallback((event) => {
                                    input.onBlur(event);
                                    onBlur && onBlur(event);
                                }, [input]
                            );

                            const onCombinedFocus: Types.TOnCombinedFocus = useCallback((event) => {
                                    input.onFocus(event);
                                    onFocus && onFocus(event);
                                }, [input]);

                            const onCombinedChange = useCallback(
                                <T extends HTMLElement = HTMLElement>(event: React.ChangeEvent<T>): void => {
                                    input.onChange(event);
                                    onChange && onChange(event);

                                    // !important. We need to fire blur handler manually after change event or validation will be blink upon clicking
                                    if (type === 'checkbox' || options.type === 'checkbox') {
                                        onCombinedBlur(event as React.FocusEvent<T>);
                                    }
                                }, [input, onCombinedBlur]);

                            return (
                                <Wrapped
                                    {...(formFieldProps as Exclude<P, 'onChange' | 'onBlur' | 'onFocus' | 'value' | 'type'>)}
                                    meta={meta}
                                    name={input.name}
                                    type={input.type}
                                    value={input.value}
                                    checked={input.checked}
                                    onBlur={onCombinedBlur}
                                    multiple={input.multiple}
                                    onFocus={onCombinedFocus}
                                    onChange={onCombinedChange}
                                />
                            );
                        }}
                    </Field>
                );
            };
